/**
 * PWAInstallPrompt — captures the `beforeinstallprompt` event and shows
 * a subtle CTA the user can dismiss. iOS Safari doesn't fire the event,
 * so on iOS Safari we detect Standalone-mode-off + Safari UA and show a
 * "How to Add to Home Screen" hint instead.
 *
 * Auto-hides forever once user dismisses or installs.
 */
import { useEffect, useState } from "react";
import { X, Download, Share } from "lucide-react";

const DISMISS_KEY = "md-a2hs-dismissed";
const DISMISS_UNTIL_KEY = "md-a2hs-dismissed-until";

function isStandalone() {
    return window.matchMedia("(display-mode: standalone)").matches ||
           window.navigator.standalone === true;
}

function isIOSSafari() {
    const ua = window.navigator.userAgent;
    return /iP(hone|ad|od)/i.test(ua) && /WebKit/i.test(ua) && !/CriOS|FxiOS|OPT/i.test(ua);
}

export default function PWAInstallPrompt() {
    const [event, setEvent]     = useState(null);
    const [visible, setVisible] = useState(false);
    const [iosHint, setIosHint] = useState(false);

    useEffect(() => {
        if (isStandalone()) return;
        if (localStorage.getItem(DISMISS_KEY)) return;
        const dismissedUntil = Number(localStorage.getItem(DISMISS_UNTIL_KEY) || 0);
        if (dismissedUntil > Date.now()) return;

        const handleBIP = (e) => {
            e.preventDefault();
            setEvent(e);
            setVisible(true);
        };
        window.addEventListener("beforeinstallprompt", handleBIP);

        // iOS Safari never fires beforeinstallprompt — show a manual hint
        // after 30s so we don't interrupt first-time users.
        if (isIOSSafari()) {
            const t = setTimeout(() => { setIosHint(true); setVisible(true); }, 30000);
            return () => { clearTimeout(t); window.removeEventListener("beforeinstallprompt", handleBIP); };
        }
        return () => window.removeEventListener("beforeinstallprompt", handleBIP);
    }, []);

    async function install() {
        if (!event) return;
        event.prompt();
        const { outcome } = await event.userChoice;
        if (outcome === "accepted") localStorage.setItem(DISMISS_KEY, "1");
        setVisible(false);
    }
    function dismiss() {
        // Snooze for 14 days
        localStorage.setItem(DISMISS_UNTIL_KEY, String(Date.now() + 14 * 24 * 3600 * 1000));
        setVisible(false);
    }

    if (!visible) return null;

    return (
        <div
            data-testid="pwa-install-prompt"
            className="fixed bottom-20 lg:bottom-4 left-3 right-3 lg:left-auto lg:right-4 lg:w-80 z-50 rounded-xl border border-primary/40 bg-card shadow-2xl p-4 animate-in slide-in-from-bottom-2"
        >
            <button
                onClick={dismiss}
                data-testid="pwa-install-dismiss"
                className="absolute top-2 right-2 w-8 h-8 rounded-full hover:bg-secondary flex items-center justify-center"
                aria-label="Dismiss"
            >
                <X className="w-4 h-4" />
            </button>
            <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary text-primary-foreground flex items-center justify-center font-extrabold text-xs shrink-0">MD</div>
                <div className="flex-1 min-w-0 pr-6">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-primary mb-1">Install Melian</div>
                    <div className="text-sm font-semibold leading-tight">Add to Home Screen</div>
                    <p className="text-xs text-muted-foreground mt-1">
                        {iosHint
                            ? <>Tap <Share className="w-3 h-3 inline" /> Share → &ldquo;Add to Home Screen&rdquo; to install the app icon.</>
                            : "Get an app icon and native fullscreen. No app store — installs in a second."}
                    </p>
                    {!iosHint && (
                        <button
                            onClick={install}
                            data-testid="pwa-install-btn"
                            className="mt-3 inline-flex items-center gap-1.5 min-h-[36px] px-3 rounded-lg bg-primary text-primary-foreground text-xs font-bold hover:bg-primary/90"
                        >
                            <Download className="w-3.5 h-3.5" />
                            Install app
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
